// [2026-08-07] ADDED — every chart in the dashboard.
//
// THE RULES THESE ARE BUILT TO, AND WHY
// -------------------------------------
// * NO DUAL AXIS, ANYWHERE. Two measures on two y-scales let a reader draw a comparison the data
//   does not support — the crossings are an artefact of the scaling. Where two measures do not
//   share a unit they get two charts. The latency chart plots avg and p95 together only because
//   both are milliseconds on one scale.
// * COLOUR FOLLOWS THE ENTITY, NEVER ITS RANK. Series colours are assigned by name, so filtering
//   a screen cannot repaint the survivors and make a reader think a series changed.
// * A LEGEND WHENEVER THERE ARE 2+ SERIES; none for one (the title names it). Identity is never
//   carried by colour alone.
// * STATUS COLOURS ARE RESERVED. good/warning/critical mean outcome, never "series 4".
// * RECESSIVE CHROME. Hairline horizontal grid only, no vertical rules, no axis lines competing
//   with the data.
//
// The palette below is duplicated from src/theme.css because SVG attributes rendered by Recharts
// need concrete values. THEY MUST BE KEPT IN SYNC — the hexes were validated together against the
// white card surface (CVD ΔE 9.2 worst pair, normal-vision 24.0) and changing one here without the
// other silently breaks a result that was measured, not chosen.
//
// EVERY SERIES SETS isAnimationActive={false}. THIS IS DELIBERATE — do not remove it.
// Recharts replays its entry animation whenever the chart re-renders, including when
// ResponsiveContainer re-measures after a resize. Screens here refetch on a range change and the
// upload screen polls every 2.5s, so with animation on the marks re-grow from zero on each update:
// visually noisy in use, and it renders as an EMPTY chart to anything that samples mid-animation
// (a screenshot, a visual regression test, a slow machine). A dashboard that intermittently
// appears to show no data is worse than one that never animates.
// Note the two Bars carrying <Cell> children need the prop set on the OPENING tag — they are not
// self-closing, and missing one there is exactly how "requests by hour of day" rendered blank.

import { useState } from 'react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { WEEKDAYS, clock, ms, num, shortDate } from './ui.jsx'

export const PALETTE = {
  series1: '#2a78d6',   // blue
  series2: '#eb6834',   // orange
  series3: '#1baf7a',   // aqua — sub-3:1 on white, always paired with labels or a table
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',
  grid: '#ecebe5',
  axis: '#c3c2b7',
  muted: '#898781',
  ink: '#0b0b0b',
  seq: ['#f4f7fb', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'],
}

// [2026-08-11] ADDED — click a legend entry to hide or show that series.
//
// WHY THIS AND NOT ZOOM/BRUSH. On a stacked traffic chart the useful question is almost always
// "how much of this is errors" — and the honest way to answer it is to take the dominant series
// away and re-read the axis. A brush answers a different question (when did it happen) that the
// date axis already answers. This is one hook, it works on every multi-series chart here, and it
// cannot mislead: hiding a series changes the axis, which is visible.
//
// Returns the props a chart needs plus a predicate. Series call `hidden('key')` and pass the
// result to Recharts' own `hide` prop, so the library handles the re-scale.
function useSeriesToggle() {
  const [off, setOff] = useState(() => new Set())
  const toggle = (key) => {
    if (!key) return
    setOff((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  return { hidden: (key) => off.has(key), toggle }
}

const AXIS_TICK = { fontSize: 11, fill: PALETTE.muted }
const GRID = { stroke: PALETTE.grid, strokeDasharray: '0' }

// [2026-09-04] ADDED — an OPTIONAL per-chart colour override, for the revamped customer dashboard.
//
// WHY A PROP AND NOT AN EDIT TO `PALETTE` ABOVE.
// PALETTE is module state shared by every chart in BOTH apps: src/index.js re-exports this file and
// proctoring-frontend-admin renders these same components on its staff screens. Re-assigning the
// hexes to match the IncProctor Figma would silently repaint that repository too, and the change
// would land at ITS build rather than this one's.
//
// So the default is UNCHANGED and every existing call site — all of the admin app's, and every one
// of this app's older screens — keeps the exact palette recorded in the header above, including its
// measured CVD result. Only a caller that explicitly passes `colors` gets anything different.
//
// ⚠ THE VALIDATION DOES NOT TRANSFER. The ΔE figures at the top of this file were measured for the
// blue/orange/aqua set against a white card. A caller passing its own hues is responsible for its
// own contrast and colour-blind separation; passing three colours "that look nice" silently voids a
// result that was measured rather than chosen. The customer dashboard's set is declared once, with
// that reasoning, in views/platform/PlatformDashboard.jsx.
const withColors = (overrides) => (overrides ? { ...PALETTE, ...overrides } : PALETTE)

/** One tooltip for every chart, so the hover layer reads identically across the dashboard.
 *  `formatter` converts a raw value into its display string — the caller owns the unit. */
function ChartTooltip({ active, payload, label, title, formatter = num }) {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tooltip">
      <div className="t-title">{title ? title(label, payload) : label}</div>
      {payload.map((entry) => (
        <div className="t-row" key={entry.dataKey}>
          <span className="k">
            <span className="swatch" style={{ background: entry.color || entry.fill }} />
            {entry.name}
          </span>
          <span className="v">{formatter(entry.value)}</span>
        </div>
      ))}
    </div>
  )
}

/** The shared legend.
 *
 *  [2026-08-11] CHANGED — entries are now BUTTONS that hide and show their series when `onToggle`
 *  is supplied. Without it they render exactly as before, so the charts that pass no handler are
 *  untouched.
 *
 *  WHY BUTTONS AND NOT SPANS. This is a real control now, so it has to be reachable by keyboard and
 *  announced as pressable — a chart control that only works with a mouse excludes exactly the
 *  reviewers who most need the data in another form. `aria-pressed` carries the state, and a hidden
 *  series is dimmed AND struck through, so the state is never carried by opacity alone.
 *
 *  --- OLD (pre-2026-08-11) ---
 *  function Legends({ items }) {
 *    return (
 *      <div className="legend" style={{ marginTop: 8 }}>
 *        {items.map((item) => (
 *          <span className="item" key={item.label}>
 *            <span className="swatch" style={{ background: item.color }} />{item.label}
 *          </span>
 *        ))}
 *      </div>
 *    )
 *  }
 *  --- END OLD --- */
function Legends({ items, onToggle, isHidden }) {
  return (
    <div className="legend" style={{ marginTop: 8 }}>
      {items.map((item) => {
        const key = item.dataKey || item.label
        const hidden = isHidden ? isHidden(key) : false
        if (!onToggle) {
          return (
            <span className="item" key={item.label}>
              <span className="swatch" style={{ background: item.color }} />{item.label}
            </span>
          )
        }
        return (
          <button
            type="button"
            className="item"
            key={item.label}
            aria-pressed={!hidden}
            title={hidden ? `Show ${item.label}` : `Hide ${item.label}`}
            onClick={() => onToggle(key)}
            style={{
              background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer',
              opacity: hidden ? 0.45 : 1,
              textDecoration: hidden ? 'line-through' : 'none',
            }}
          >
            <span className="swatch" style={{ background: item.color }} />{item.label}
          </button>
        )
      })}
    </div>
  )
}

// ── request volume by outcome ─────────────────────────────────────────────────
// Stacked, because the three parts genuinely sum to "all requests". Success is the blue series;
// the two failure classes take the reserved status colours, since they ARE outcomes rather than
// arbitrary categories. Client errors and server errors stay apart deliberately: a spike in 4xx is
// an integration problem to help a customer with, a spike in 5xx is an incident.

// [2026-09-04] CHANGED — takes an optional `colors`. OLD signature: ({ data, height = 240 }).
// Omitted, it resolves to PALETTE and this renders byte-for-byte what it did before.
export function TrafficChart({ data, height = 240, colors }) {
  // [2026-08-11] ADDED — success usually dwarfs both error series, so the only way to read the
  // error shape is to take success away and let the axis re-scale. That is one click now.
  const { hidden, toggle } = useSeriesToggle()
  const C = withColors(colors)
  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid {...GRID} vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK}
                 tickLine={false} axisLine={{ stroke: C.axis }} minTickGap={24} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
          <Tooltip content={<ChartTooltip title={(label) => shortDate(label)} />}
                   cursor={{ stroke: C.axis, strokeWidth: 1 }} />
          <Area type="monotone" dataKey="success" name="Success" stackId="1" hide={hidden('success')}
                stroke={C.series1} fill={C.series1} fillOpacity={0.16} strokeWidth={2} isAnimationActive={false} />
          <Area type="monotone" dataKey="client_error" name="Client errors (4xx)" stackId="1" hide={hidden('client_error')}
                stroke={C.warning} fill={C.warning} fillOpacity={0.2} strokeWidth={2} isAnimationActive={false} />
          <Area type="monotone" dataKey="server_error" name="Server errors (5xx)" stackId="1" hide={hidden('server_error')}
                stroke={C.critical} fill={C.critical} fillOpacity={0.2} strokeWidth={2} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
      <Legends onToggle={toggle} isHidden={hidden} items={[
        { label: 'Success', dataKey: 'success', color: C.series1 },
        { label: 'Client errors (4xx)', dataKey: 'client_error', color: C.warning },
        { label: 'Server errors (5xx)', dataKey: 'server_error', color: C.critical },
      ]} />
    </>
  )
}

// ── latency trend ─────────────────────────────────────────────────────────────
// Average AND p95 on ONE axis — both are milliseconds, so the comparison is honest and the gap
// between the two lines is the actual point: a flat average with a climbing p95 is a tail problem
// that an average-only chart hides completely.

// [2026-09-04] CHANGED — takes optional `colors` and `variant`.
// OLD signature: ({ data, height = 240 }) — and `variant` defaults to 'line', so every existing
// call site (the admin app's and this app's analytics screen) renders exactly what it did before.
//
// WHY A BAR VARIANT AT ALL. The IncProctor Figma draws this panel as grouped bars rather than two
// lines. Both readings are honest here because the rule that governs this chart is unchanged: avg
// and p95 are BOTH milliseconds on ONE axis, so the comparison is real either way (see the no-dual-
// axis rule at the top of this file). What bars lose is the emphasis on the GAP between the two
// series over time — a flat average with a climbing p95 is a tail problem, and it is easier to see
// as two diverging lines than as pairs of bars. That is why 'line' stays the default.
export function LatencyTrend({ data, height = 240, colors, variant = 'line' }) {
  const hasData = (data || []).some((row) => row.avg_ms !== null || row.p95_ms !== null)
  if (!hasData) return <NoData height={height} message="No latency recorded in this window." />
  const C = withColors(colors)
  const legend = (
    <Legends items={[
      { label: 'Average', color: C.series1 },
      { label: 'p95 (estimated)', color: C.series2 },
    ]} />
  )

  if (variant === 'bars') {
    return (
      <>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: -12 }}
                    barCategoryGap="22%">
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK}
                   tickLine={false} axisLine={{ stroke: C.axis }} minTickGap={16} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52}
                   tickFormatter={(value) => (value >= 1000 ? `${value / 1000}s` : value)} />
            <Tooltip content={<ChartTooltip title={(label) => shortDate(label)} formatter={ms} />}
                     cursor={{ fill: 'rgba(19,63,125,.06)' }} />
            <Bar dataKey="avg_ms" name="Average" fill={C.series1} radius={[3, 3, 0, 0]}
                 isAnimationActive={false} />
            <Bar dataKey="p95_ms" name="p95" fill={C.series2} radius={[3, 3, 0, 0]}
                 isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
        {legend}
      </>
    )
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid {...GRID} vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK}
                 tickLine={false} axisLine={{ stroke: C.axis }} minTickGap={24} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52}
                 tickFormatter={(value) => (value >= 1000 ? `${value / 1000}s` : value)} />
          <Tooltip content={<ChartTooltip title={(label) => shortDate(label)} formatter={ms} />}
                   cursor={{ stroke: C.axis, strokeWidth: 1 }} />
          {/* connectNulls is deliberately OFF: a day with no traffic has no latency, and joining
              across it would draw a trend through a gap where nothing was measured. */}
          <Line type="monotone" dataKey="avg_ms" name="Average" stroke={C.series1}
                strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="p95_ms" name="p95" stroke={C.series2}
                strokeWidth={2} strokeDasharray="5 3" dot={false} activeDot={{ r: 4 }}
                connectNulls={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      {legend}
    </>
  )
}

// ── latency distribution ──────────────────────────────────────────────────────
// One series, so no legend — the title names it. Bucket labels are the x categories, which makes
// the shape of the tail directly readable rather than something to infer from a percentile.

// [2026-09-04] CHANGED — takes optional `colors` and `ramp`. OLD: ({ distribution, height = 200 }).
//
// `ramp` renders each bucket in the SEQUENTIAL blue ramp instead of one flat series colour, which
// is what the IncProctor Figma draws. It is off by default. The ramp is legitimate here and only
// here: the x categories are ordered magnitude bands (0–10ms … 500–1000ms), so light-to-dark
// encodes the same quantity the axis already does — redundant, not invented. Do NOT copy this to a
// chart whose categories are unordered; a ramp across arbitrary categories asserts a progression
// the data does not have.
export function LatencyHistogram({ distribution, height = 200, colors, ramp = false }) {
  const data = (distribution || []).map((row) => ({
    ...row, label: row.bucket.endsWith('+') ? `${row.bucket.replace('+', '')}ms+` : `${row.bucket}ms`,
  }))
  const total = data.reduce((sum, row) => sum + row.count, 0)
  if (!total) return <NoData height={height} message="No latency samples in this window." />
  const C = withColors(colors)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }} barCategoryGap={2}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ ...AXIS_TICK, fontSize: 10 }} tickLine={false}
               axisLine={{ stroke: C.axis }} interval={0} angle={-35} textAnchor="end" height={52} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
        <Tooltip content={<ChartTooltip title={(label) => `${label} response time`} />}
                 cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        {/* 4px rounded data-end anchored to the baseline — the bar grows from zero, so only the
            top corners round. */}
        <Bar dataKey="count" name="Requests" fill={C.series1} radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {ramp && data.map((row, index) => (
            // Step from 1, never 0: index 0 of the ramp is the "empty cell" tint, and a bucket with
            // real samples painted in it reads as no data.
            <Cell key={row.bucket}
                  fill={C.seq[Math.min(index + 1, C.seq.length - 1)]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── traffic by hour of day ────────────────────────────────────────────────────
// The answer to "when do we actually get requests". The peak hour is given the orange series
// colour AND a text label above the page — never colour alone.

// [2026-09-04] CHANGED — takes an optional `colors`. OLD: ({ data, height = 200 }).
export function HourOfDayChart({ data, height = 200, colors }) {
  const rows = (data || []).map((row) => ({ ...row, label: `${String(row.hour).padStart(2, '0')}:00` }))
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  if (!total) return <NoData height={height} message="No requests recorded yet." />
  const C = withColors(colors)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -12 }} barCategoryGap={2}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ ...AXIS_TICK, fontSize: 10 }} tickLine={false}
               axisLine={{ stroke: C.axis }} interval={2} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
        <Tooltip content={<ChartTooltip title={(label) => `${label} – requests`} />}
                 cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="count" name="Requests" radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {rows.map((row) => (
            <Cell key={row.hour} fill={row.is_peak ? C.series2 : C.series1} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── weekday x hour heatmap ────────────────────────────────────────────────────
// A SEQUENTIAL single-hue ramp, light to dark, because the encoded quantity is magnitude. Never a
// rainbow: a multi-hue scale invents category boundaries where the data has none.
//
// The full 7x24 grid is always drawn, including empty cells — a heatmap with holes reads as
// "no data" when it means "no traffic".

// [2026-09-04] CHANGED — takes an optional `colors`. OLD: ({ data, height }).
// Only `seq` is read here, so a caller overriding the ramp must supply the FULL eight-step array;
// a shorter one still works (the indexing clamps) but loses resolution at the top end.
export function Heatmap({ data, height, colors }) {
  const cells = data || []
  const max = cells.reduce((best, cell) => Math.max(best, cell.count), 0)
  const C = withColors(colors)

  const colour = (count) => {
    if (!max || !count) return C.seq[0]
    // Index into the ramp by relative magnitude. Step 1 is the floor for any non-zero count, so a
    // cell with real traffic never renders as the "empty" colour.
    const step = Math.ceil((count / max) * (C.seq.length - 1))
    return C.seq[Math.min(Math.max(step, 1), C.seq.length - 1)]
  }

  const byCell = new Map(cells.map((cell) => [`${cell.weekday}-${cell.hour}`, cell.count]))

  return (
    <div style={height ? { minHeight: height } : undefined}>
      <div className="heatmap">
        <span />
        {Array.from({ length: 24 }, (_, hour) => (
          <span className="hm-col-label" key={`h${hour}`}>{hour % 3 === 0 ? hour : ''}</span>
        ))}
        {WEEKDAYS.map((day, weekday) => (
          <FragmentRow key={day} day={day} weekday={weekday} byCell={byCell} colour={colour} />
        ))}
      </div>
      <div className="legend" style={{ marginTop: 10, alignItems: 'center' }}>
        <span className="muted">Fewer</span>
        {C.seq.map((step) => (
          <span key={step} className="swatch"
                style={{ background: step, width: 13, height: 13, border: '1px solid #e6e5e0' }} />
        ))}
        <span className="muted">More — peak {num(max)} requests in one hour</span>
      </div>
    </div>
  )
}

function FragmentRow({ day, weekday, byCell, colour }) {
  return (
    <>
      <span className="hm-axis">{day}</span>
      {Array.from({ length: 24 }, (_, hour) => {
        const count = byCell.get(`${weekday}-${hour}`) || 0
        return (
          <span key={hour} className="hm-cell" style={{ background: colour(count) }}
                title={`${day} ${String(hour).padStart(2, '0')}:00 — ${num(count)} requests`} />
        )
      })}
    </>
  )
}

// ── credit spend over time ────────────────────────────────────────────────────
// One series, no legend. Reads the same counter the billing tiles use, so the chart and the
// invoice cannot disagree.

export function CreditsChart({ data, height = 200 }) {
  const total = (data || []).reduce((sum, row) => sum + (row.credits || 0), 0)
  if (!total) return <NoData height={height} message="No credits spent in this window." />
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK}
               tickLine={false} axisLine={{ stroke: PALETTE.axis }} minTickGap={24} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} />
        <Tooltip content={<ChartTooltip title={(label) => shortDate(label)}
                                        formatter={(value) => `${num(value, { decimals: 2 })} credits`} />}
                 cursor={{ stroke: PALETTE.axis, strokeWidth: 1 }} />
        <Area type="monotone" dataKey="credits" name="Credits" stroke={PALETTE.series3}
              fill={PALETTE.series3} fillOpacity={0.15} strokeWidth={2} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// ── spend per client ──────────────────────────────────────────────────────────
// Horizontal bars, sorted, so the labels are readable at any name length. Ships beside a table on
// the page — the aqua series sits below 3:1 on white, and the table IS the required relief.

export function SpendByClientChart({ data, height = 260 }) {
  const rows = (data || []).slice(0, 10).map((row) => ({
    name: row.client_id, credits: row.credits, calls: row.total,
  }))
  if (!rows.length) return <NoData height={height} message="No client spend in this window." />
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
                barCategoryGap={3}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: PALETTE.axis }} />
        <YAxis type="category" dataKey="name" tick={{ ...AXIS_TICK, fontSize: 11 }}
               tickLine={false} axisLine={false} width={116} />
        <Tooltip content={<ChartTooltip formatter={(value) => `${num(value, { decimals: 2 })} credits`} />}
                 cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="credits" name="Credits spent" fill={PALETTE.series1} radius={[0, 4, 4, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── findings across the recording ─────────────────────────────────────────────
// Per-segment violation counts, aligned to the same minute buckets the timeline below it uses, so
// the chart and the list are literally the same data at two zoom levels.

export function FindingsPerSegmentChart({ timeline, height = 150 }) {
  const rows = (timeline || []).map((segment) => ({
    label: clock(segment.start_offset_sec),
    count: segment.counts.total,
    severity: segment.highest_severity,
  }))
  if (!rows.length) return null
  const tone = { high: PALETTE.critical, medium: PALETTE.warning, low: PALETTE.axis }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -18 }} barCategoryGap={2}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="label" tick={{ ...AXIS_TICK, fontSize: 10 }} tickLine={false}
               axisLine={{ stroke: PALETTE.axis }} interval={0} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={44} allowDecimals={false} />
        <Tooltip content={<ChartTooltip title={(label) => `From ${label}`} />}
                 cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="count" name="Findings" radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {rows.map((row, index) => (
            <Cell key={index} fill={tone[row.severity] || PALETTE.seq[1]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

function NoData({ height, message }) {
  return (
    <div style={{ height, display: 'grid', placeItems: 'center' }}>
      <span className="muted" style={{ fontSize: 13 }}>{message}</span>
    </div>
  )
}

// ── [2026-08-07] proctoring-outcome charts ────────────────────────────────────
// These measure the PRODUCT, not the integration. Everything above answers "is the API healthy";
// these answer "what did we actually find in the recordings", which is what the service is for.

/** Findings by type across every analysis. One series, so no legend — the axis names each bar. */
export function FindingsByTypeChart({ data, height = 220 }) {
  const rows = (data || []).map((row) => ({
    ...row, label: (row.type || '').replace(/_/g, ' '),
  }))
  if (!rows.length) return <NoData height={height} message="No findings recorded yet." />
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
                barCategoryGap={3}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis type="number" tick={AXIS_TICK} tickLine={false}
               axisLine={{ stroke: PALETTE.axis }} allowDecimals={false} />
        <YAxis type="category" dataKey="label" tick={{ ...AXIS_TICK, fontSize: 11 }}
               tickLine={false} axisLine={false} width={124} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="count" name="Findings" fill={PALETTE.series1} radius={[0, 4, 4, 0]}
             isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Suspicion-score distribution. Bands, not raw scores — a histogram of every integer is noise.
 *  Uses the RESERVED status colours because these bands are outcomes, not arbitrary categories. */
export function ScoreBandChart({ data, height = 220 }) {
  const rows = data || []
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  if (!total) return <NoData height={height} message="No completed analyses yet." />
  const tone = {
    'clean (0)': PALETTE.good, 'low (1-3)': PALETTE.series1,
    'medium (4-6)': PALETTE.warning, 'high (7-10)': PALETTE.critical,
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -12 }} barCategoryGap={2}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="band" tick={{ ...AXIS_TICK, fontSize: 11 }} tickLine={false}
               axisLine={{ stroke: PALETTE.axis }} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
        <Tooltip content={<ChartTooltip title={(l) => `Suspicion score ${l}`} />}
                 cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="count" name="Recordings" radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {rows.map((row) => <Cell key={row.band} fill={tone[row.band] || PALETTE.series1} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Analyses per day split clean vs flagged. Stacked, because the two genuinely sum to the total. */
export function AnalysisTrendChart({ data, height = 220 }) {
  const rows = (data || []).map((row) => ({ ...row, flagged: row.analyses - row.clean }))
  if (!rows.length) return <NoData height={height} message="No analyses in this window." />
  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -12 }} barCategoryGap={2}>
          <CartesianGrid {...GRID} vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK} tickLine={false}
                 axisLine={{ stroke: PALETTE.axis }} minTickGap={20} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
          <Tooltip content={<ChartTooltip title={(l) => shortDate(l)} />}
                   cursor={{ fill: 'rgba(42,120,214,.06)' }} />
          <Bar dataKey="clean" name="Clean" stackId="a" fill={PALETTE.good}
               radius={[0, 0, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="flagged" name="Flagged" stackId="a" fill={PALETTE.critical}
               radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <Legends items={[
        { label: 'Clean', color: PALETTE.good },
        { label: 'Flagged', color: PALETTE.critical },
      ]} />
    </>
  )
}

// [2026-08-10] ADDED — candidate sessions per day, for the browser-SDK reports screen.
//
// ONE SERIES, SO NO LEGEND — the card title names it, per the rules at the top of this file. Uses
// series1 rather than a new hue: the palette's three-colour result was validated as a set, and
// adding a fourth by eye would break a measurement rather than extend it.
// [2026-09-04] CHANGED — takes an optional `colors`. OLD: ({ data, height = 220 }).
//
// NOTE the gradient id is now derived from the resolved colour rather than being the constant
// "candidateFill". SVG gradient ids are DOCUMENT-global: with two of these charts on one page (the
// revamped dashboard renders this beside the analytics screen's copy in some states) the first
// <defs> wins and the second chart silently paints in the first one's colour.
export function CandidateSessionsChart({ data, height = 220, colors }) {
  const rows = data || []
  if (!rows.length) return <NoData height={height} message="No candidate sessions in this window." />
  const C = withColors(colors)
  const fillId = `candidateFill-${String(C.series1).replace('#', '')}`
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.series1} stopOpacity={0.22} />
            <stop offset="100%" stopColor={C.series1} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS_TICK} tickLine={false}
               axisLine={{ stroke: C.axis }} minTickGap={20} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} allowDecimals={false} />
        <Tooltip content={<ChartTooltip title={(l) => shortDate(l)} />}
                 cursor={{ stroke: C.axis }} />
        <Area type="monotone" dataKey="sessions" name="Sessions" stroke={C.series1}
              strokeWidth={2} fill={`url(#${fillId})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

/** Severity mix across all findings. Reserved status colours, and the count is always labelled. */
export function SeverityMixChart({ data, height = 180 }) {
  const rows = (data || []).filter((row) => row.count > 0)
  if (!rows.length) return <NoData height={height} message="No findings to break down." />
  const tone = { high: PALETTE.critical, medium: PALETTE.warning, low: PALETTE.axis }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
                barCategoryGap={4}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis type="number" tick={AXIS_TICK} tickLine={false}
               axisLine={{ stroke: PALETTE.axis }} allowDecimals={false} />
        <YAxis type="category" dataKey="severity" tick={{ ...AXIS_TICK, fontSize: 11 }}
               tickLine={false} axisLine={false} width={70} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Bar dataKey="count" name="Findings" radius={[0, 4, 4, 0]} isAnimationActive={false}>
          {rows.map((row) => <Cell key={row.severity} fill={tone[row.severity] || PALETTE.series1} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Endpoint health: successes, client errors and server errors per endpoint, on ONE stacked bar.
 *
 *  [2026-08-11] ADDED for the operator health screen.
 *
 *  STACKED RATHER THAN GROUPED, and the reason is the question being asked. An operator scanning
 *  this wants "which endpoint is failing, and is it their fault or ours" — a stack shows the
 *  proportion of a single endpoint's traffic at a glance, where grouped bars force a comparison
 *  across bars that nobody is asking for.
 *
 *  CLIENT AND SERVER ERRORS ARE NEVER MERGED. A 4xx is usually an integration problem at the other
 *  end; a 5xx is ours. Collapsing them into "errors" is how a customer sending malformed payloads
 *  comes to look like an outage — and how a real outage gets ignored as "that endpoint is always
 *  red". They get the reserved warning and critical colours respectively, in that order, so the
 *  eye lands on the serious half.
 *
 *  Height scales with row count: at a fixed height Recharts silently drops every other category
 *  label, which on a health screen means an operator reads a failure against the wrong endpoint.
 */
export function EndpointHealthChart({ data, height }) {
  const rows = (data || []).map((row) => {
    const total = row.total || 0
    const server = row.server_errors || 0
    const client = row.client_errors || 0
    return {
      // Endpoints share long prefixes ("/api/v1/proctoring/..."), and the distinguishing part is
      // the tail. Truncating from the LEFT keeps what tells them apart.
      label: (row.endpoint || '').length > 30
        ? '…' + (row.endpoint || '').slice(-29)
        : (row.endpoint || ''),
      endpoint: row.endpoint,
      ok: Math.max(total - server - client, 0),
      client_errors: client,
      server_errors: server,
    }
  })
  if (!rows.length) return <NoData height={height || 200} message="No endpoint traffic yet." />

  return (
    <ResponsiveContainer width="100%" height={height || Math.max(200, rows.length * 30)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
                barCategoryGap={4}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis type="number" tick={AXIS_TICK} tickLine={false}
               axisLine={{ stroke: PALETTE.axis }} allowDecimals={false} />
        <YAxis type="category" dataKey="label" tick={{ ...AXIS_TICK, fontSize: 10.5 }}
               tickLine={false} axisLine={false} width={210} interval={0} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(42,120,214,.06)' }} />
        <Legend wrapperStyle={{ fontSize: 11.5, paddingTop: 6 }} />
        <Bar dataKey="ok" stackId="calls" name="Succeeded" fill={PALETTE.series1}
             isAnimationActive={false} />
        <Bar dataKey="client_errors" stackId="calls" name="Client errors (4xx)"
             fill={PALETTE.warning} isAnimationActive={false} />
        <Bar dataKey="server_errors" stackId="calls" name="Server errors (5xx)"
             fill={PALETTE.critical} radius={[0, 4, 4, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}
